import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Grid,
    Paper,
    Typography,
    Stack,
    Button,
    Chip,
    Divider,
    Avatar,
    IconButton,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Snackbar
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowBack, Verified, Block, Phone, Mail, Home, People, Close, ZoomIn, OpenInNew, LocationOn } from '@mui/icons-material';
import userService from 'api/userService';
import { useAdminRiderDetails, useAdminRiderKYC } from 'hooks/useRiders';
import kycService from 'api/kycService';
import WalletFundManagement from 'views/admin/components/WalletFundManagement';
import BankAccountManager from 'views/common/bank-accounts/BankAccountManager';
import MapLocationPicker from 'views/common/MapLocationPicker';

export default function RiderDetailPage() {
    const { riderId } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [zoomImage, setZoomImage] = useState(null);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    const [isRejectModalOpen, setRejectModalOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState('');

    const { data: riderRes, isLoading: riderLoading, refetch: refetchRider } = useAdminRiderDetails(riderId);
    const { data: kycRes, isLoading: kycLoading, refetch: refetchKyc } = useAdminRiderKYC(riderRes?.data?.userId);

    const rider = riderRes?.data;
    const kyc = kycRes?.data;

    const updateRiderMutation = useMutation({
        mutationFn: (payload) => userService.adminUpdateRider(rider.id, payload),
        onSuccess: async () => {
            await queryClient.invalidateQueries(['adminRiderDetails', riderId]);
            setNotification({ open: true, message: 'Rider profile updated successfully', severity: 'success' });
        },
        onError: (err) => setNotification({ open: true, message: err?.response?.data?.message || 'Update failed', severity: 'error' })
    });

    const handleCloseSnack = () => setNotification({ ...notification, open: false });

    const handleUpdateKycStatus = async (status, reason = '') => {
        setIsSubmitting(true);
        try {
            await kycService.adminUpdateKycStatus(kyc.id, { status, message: reason });
            setNotification({
                open: true,
                message: `KYC successfully updated to ${status}`,
                severity: status === 'verified' ? 'success' : 'info'
            });
            await Promise.all([refetchRider(), refetchKyc()]);
            setRejectModalOpen(false);
            setRejectReason('');
        } catch (error) {
            setNotification({
                open: true,
                message: 'Update failed. Please try again.',
                severity: 'error'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (riderLoading || kycLoading) {
        return (
            <Box p={5} textAlign="center">
                <CircularProgress />
            </Box>
        );
    }

    const userDisplay = kyc?.user || rider?.user || {};

    return (
        <Box p={4} sx={{ background: '#f5f6fa', minHeight: '100vh' }}>
            {!kyc && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    No KYC record found for this rider. They need to submit their KYC application.
                </Alert>
            )}
            {/* Header Section */}
            <Stack direction="row" justifyContent="space-between" mb={4} alignItems="center">
                <Stack direction="row" spacing={2} alignItems="center">
                    <IconButton onClick={() => navigate('/admin/riders')} sx={{ bgcolor: 'white' }}>
                        <ArrowBack />
                    </IconButton>
                    <Box>
                        <Typography variant="h4" fontWeight={800}>
                            {userDisplay?.firstName} {userDisplay?.lastName}
                        </Typography>
                        <Stack direction="row" spacing={1} mt={0.5}>
                            <Chip
                                label={`KYC: ${kyc ? kyc.status?.toUpperCase() : 'NO KYC'}`}
                                color={kyc?.status === 'verified' ? 'success' : kyc?.status === 'rejected' ? 'error' : 'warning'}
                                size="small"
                                sx={{ fontWeight: 700 }}
                            />
                            <Chip
                                label={rider?.available ? '🟢 Online' : '🔴 Offline'}
                                color={rider?.available ? 'success' : 'default'}
                                size="small"
                                variant="outlined"
                                sx={{ fontWeight: 700 }}
                            />
                        </Stack>
                    </Box>
                </Stack>

                <Stack direction="row" spacing={2}>
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <Block />}
                        onClick={() => setRejectModalOpen(true)}
                        disabled={!kyc || isSubmitting || kyc.status === 'rejected'}
                    >
                        Reject KYC
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <Verified />}
                        onClick={() => handleUpdateKycStatus('verified')}
                        disabled={!kyc || isSubmitting || kyc.status === 'verified'}
                    >
                        Approve Overall KYC
                    </Button>
                </Stack>
            </Stack>

            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <Stack spacing={3}>
                        {kyc && kyc.nin && kyc.nin.nin ? (
                            <Paper sx={{ p: 3, borderRadius: 3, background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', color: 'white', boxShadow: '0 8px 32px rgba(30, 60, 114, 0.15)' }}>
                                <Typography variant="h6" fontWeight={700} mb={2} sx={{ opacity: 0.9 }}>
                                    National Identification Number (NIN)
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'rgba(255, 255, 255, 0.12)', p: 2.5, borderRadius: 2, border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                                    <Typography variant="h4" sx={{ letterSpacing: 4, fontFamily: 'monospace', fontWeight: 800 }}>
                                        {kyc.nin.nin}
                                    </Typography>
                                    <Chip
                                        label={kyc.ninStatus?.toUpperCase() || 'PENDING'}
                                        color={kyc.ninStatus === 'verified' ? 'success' : kyc.ninStatus === 'failed' ? 'error' : 'warning'}
                                        size="small"
                                        sx={{ fontWeight: 'bold', color: 'white', borderColor: 'white' }}
                                        variant="outlined"
                                    />
                                </Box>
                            </Paper>
                        ) : kyc ? (
                            <Alert severity="warning">
                                No NIN submitted by this rider yet.
                            </Alert>
                        ) : null}

                        <Paper sx={{ p: 3, borderRadius: 3 }}>
                            <MapLocationPicker
                                initialLat={rider?.location?.coordinates?.[1]}
                                initialLng={rider?.location?.coordinates?.[0]}
                                isLoading={updateRiderMutation.isPending}
                                onSave={(lat, lng) => updateRiderMutation.mutate({ lat, lng })}
                                label="Rider Location Settings"
                            />
                        </Paper>
                    </Stack>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Stack spacing={3}>
                        <Paper sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
                            <Avatar src={kyc?.passportUrl || userDisplay?.profilePicture} sx={{ width: 100, height: 100, mx: 'auto', mb: 2, border: '3px solid #eee' }} />
                            <Typography variant="subtitle1" fontWeight={700}>
                                {userDisplay?.firstName} {userDisplay?.lastName}
                            </Typography>
                            <Divider sx={{ my: 2 }} />
                            <Stack spacing={1.5} textAlign="left">
                                <ContactItem icon={<Phone fontSize="inherit" />} text={userDisplay?.phoneNumber || rider?.phoneNumber} />
                                <ContactItem icon={<Mail fontSize="inherit" />} text={userDisplay?.email || rider?.email} />
                                <ContactItem icon={<Home fontSize="inherit" />} text={kyc?.address?.city || rider?.city || 'N/A'} />
                            </Stack>
                            <Divider sx={{ my: 2 }} />
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                                <Typography variant="caption" color="text.secondary">Availability</Typography>
                                <Chip
                                    label={rider?.available ? '🟢 Online' : '🔴 Offline'}
                                    color={rider?.available ? 'success' : 'default'}
                                    size="small"
                                    variant={rider?.available ? 'filled' : 'outlined'}
                                    sx={{ fontWeight: 700 }}
                                />
                            </Stack>
                        </Paper>

                        <WalletFundManagement
                            userId={rider?.userId}
                            userRole="rider"
                            currentWalletBalance={rider?.wallet?.availableBalance}
                        />
                    </Stack>
                </Grid>
            </Grid>

            {/* Bank Accounts Section */}
            <Box mt={4}>
                <Paper sx={{ p: 4, borderRadius: 3 }}>
                    <Typography variant="h6" fontWeight={700} mb={3}>
                        Rider Bank Accounts
                    </Typography>
                    <BankAccountManager userId={rider?.userId} />
                </Paper>
            </Box>

            {/* Reject Modal */}
            <Dialog open={isRejectModalOpen} onClose={() => !isSubmitting && setRejectModalOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Reject Rider KYC</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Rejection Reason"
                        sx={{ mt: 1 }}
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        disabled={isSubmitting}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setRejectModalOpen(false)} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        disabled={!rejectReason.trim() || isSubmitting}
                        onClick={() => handleUpdateKycStatus('rejected', rejectReason)}
                        startIcon={isSubmitting && <CircularProgress size={16} color="inherit" />}
                    >
                        Confirm Rejection
                    </Button>
                </DialogActions>
            </Dialog>

            {/* FIXED RELIABLE ZOOM DIALOG */}
            <Dialog
                open={Boolean(zoomImage)}
                onClose={() => setZoomImage(null)}
                maxWidth="lg"
                PaperProps={{
                    sx: { bgcolor: 'transparent', boxShadow: 'none', overflow: 'hidden' }
                }}
            >
                <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', p: 1 }}>
                    <IconButton
                        onClick={() => setZoomImage(null)}
                        sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            bgcolor: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                            zIndex: 10
                        }}
                    >
                        <Close fontSize="large" />
                    </IconButton>
                    <Box
                        component="img"
                        src={zoomImage}
                        alt="Zoomed document"
                        sx={{
                            maxWidth: '100%',
                            maxHeight: '90vh',
                            objectFit: 'contain',
                            borderRadius: 1,
                            boxShadow: '0 10px 40px rgba(0,0,0,0.5)'
                        }}
                    />
                </Box>
            </Dialog>

            <Snackbar open={notification.open} autoHideDuration={4000} onClose={handleCloseSnack}>
                <Alert onClose={handleCloseSnack} severity={notification.severity} variant="filled">
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

function DocumentCard({ title, subText, imageUrl, onZoom }) {
    const displayImage = imageUrl || 'https://via.placeholder.com/300x150?text=No+Document';

    return (
        <Grid item xs={12} sm={4}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="caption" fontWeight={700}>
                    {title}
                </Typography>
                {imageUrl && (
                    <IconButton size="small" onClick={() => window.open(imageUrl, '_blank')} title="Open in new tab">
                        <OpenInNew sx={{ fontSize: 16 }} />
                    </IconButton>
                )}
            </Stack>

            <Box sx={{ position: 'relative', cursor: imageUrl ? 'zoom-in' : 'default', '&:hover .overlay': { opacity: 1 } }}>
                <Paper
                    variant="outlined"
                    onClick={() => imageUrl && onZoom(imageUrl)}
                    sx={{
                        height: 140,
                        borderRadius: 2,
                        mb: 1,
                        backgroundImage: `url(${displayImage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            borderColor: 'primary.main',
                            filter: imageUrl ? 'brightness(0.9)' : 'none'
                        }
                    }}
                />
                {imageUrl && (
                    <Box
                        className="overlay"
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 8,
                            bgcolor: 'rgba(0,0,0,0.3)',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0,
                            transition: '0.2s',
                            pointerEvents: 'none'
                        }}
                    >
                        <ZoomIn sx={{ color: 'white', fontSize: 32 }} />
                    </Box>
                )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                {subText}
            </Typography>
        </Grid>
    );
}

function InfoPaper({ title, icon, children }) {
    return (
        <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                {icon}
                <Typography variant="subtitle1" fontWeight={700}>
                    {title}
                </Typography>
            </Stack>
            <Stack spacing={1.5}>{children}</Stack>
        </Paper>
    );
}

function DetailRow({ label, value }) {
    return (
        <Box>
            <Typography variant="caption" color="text.secondary" display="block">
                {label}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
                {value || 'N/A'}
            </Typography>
        </Box>
    );
}

function ContactItem({ icon, text }) {
    return (
        <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ width: 32, height: 32, bgcolor: '#f0f2f5', color: 'text.primary' }}>{icon}</Avatar>
            <Typography variant="body2" fontWeight={500}>
                {text}
            </Typography>
        </Stack>
    );
}
